import {
  SearchOutlined,
  CloseOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { Command } from 'cmdk';
import { useEffect, useMemo, useRef } from 'react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Modal } from 'components/feedback/Modal';

import { useMenu } from 'hooks/utils/useMenu';

import { useAuthStore } from 'store/index';

import { useStyles } from 'layout/search/searchCommand.style';

export interface SearchRoute {
  label: string;
  path: string;
  keywords?: string[];
  section?: string;
}

interface SearchCommandProps {
  routes: SearchRoute[];
  open: boolean;
  setOpen: (open: boolean) => void;
}

export function SearchCommand({ routes, open, setOpen }: SearchCommandProps) {
  const navigate = useNavigate();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const { styles } = useStyles();
  const { userAccessibleModules } = useAuthStore();
  const { hasPathPermission } = useMenu(userAccessibleModules);

  // Filter routes based on user permissions
  const accessibleRoutes = useMemo(
    () =>
      routes.filter((route) => hasPathPermission(route.path?.replace('/', ''))),
    [routes, hasPathPermission]
  );

  // Add effect to focus input when component opens
  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  // Handle keyboard shortcuts (Cmd/Ctrl + K to open, ESC to close)
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(!open);
      }
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [setOpen, open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        searchRef.current?.focus();
      }, 100);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      footer={null}
      closeIcon={false}
      centered
      width={640}
      keyboard={false}
      styles={{ body: { padding: 0 }, container: { padding: 0 } }}
    >
      <div className={styles.cmdDialog}>
        <Command loop className={styles.cmdContent}>
          <div className={styles.inputContainer}>
            <SearchOutlined className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <Command.Input
              ref={searchRef}
              placeholder="Search pages..."
              className={styles.cmdInput}
            />
            <button
              onClick={() => setOpen(false)}
              className="rounded-sm opacity-70 hover:opacity-100"
            >
              <CloseOutlined className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
          </div>
          <Command.List className={styles.cmdList}>
            <Command.Empty className={styles.cmdEmpty}>
              No results found.
            </Command.Empty>
            <Command.Group className={styles.cmdGroup}>
              {accessibleRoutes.map((route) => (
                <Command.Item
                  key={route.path}
                  value={`${route.section ?? ''} ${route.label}`}
                  onSelect={() => {
                    navigate(route.path);
                    setOpen(false);
                  }}
                  className={styles.cmdItem}
                >
                  {route.section ? (
                    <span className="flex items-center gap-2">
                      <span className="text-gray-400 text-xs font-medium uppercase tracking-wide">
                        {route.section}
                      </span>
                      <RightOutlined className="text-gray-300 text-[10px]" />
                      <span>{route.label}</span>
                    </span>
                  ) : (
                    route.label
                  )}
                </Command.Item>
              ))}
            </Command.Group>
          </Command.List>
        </Command>
      </div>
    </Modal>
  );
}
